import {
  CheckOutlined, CloseOutlined,
  FilterOutlined,
  FormOutlined,
  QuestionOutlined,
  SettingOutlined,
  ToolOutlined,
  UserOutlined
} from '@ant-design/icons';
import {
  Button,
  Switch, Tooltip,
  Typography
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import allActions from '../../../actions';
import hasAuthenticatedRole from '../../../utils/AuthFunctions';
import ImpactForm from '../../impactForm/ImpactForm';
import styles from '../styles.module.scss';
import UserMenuContainer from '../userMenu/UserMenuContainer';
import './AppBar.css';

const { Title } = Typography;

export default function AppBar({ isProd, asMobile, closeMenu }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const userSettings = useSelector((state) => state.commonState.userSettings);
  const useDefaultSettings = useSelector((state) => state.commonState.useDefaultSettings);
  const user = useSelector((state) => state.authState.user);
  const plants = useSelector((state) => state.commonState.plants);
  const companyCodes = useSelector((state) => state.commonState.companyCodes);
  const [defaultsDisabled, setDefaultsDisabled] = useState(true);
  const [hasConfigAccess, setHasConfigAccess] = useState(false);
  const [hasViewerAccess, setHasViewerAccess] = useState(false);
  const location = useLocation().pathname;
  const [impactFormVisible, setImpactFormVisible] = useState(false);

  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    if (user?.idTokenClaims?.roles) {
      const userHasAuthenticatedRole = hasAuthenticatedRole(
        user.idTokenClaims.roles,
        process.env.REACT_APP_CONFIG_ACCESS,
      );
      setHasConfigAccess(userHasAuthenticatedRole);
      const userHasViewerAccess = hasAuthenticatedRole(
        user.idTokenClaims.roles,
        process.env.REACT_APP_VIEW_PRICES,
      );
      setHasViewerAccess(userHasViewerAccess);
    }
  }, [user?.idTokenClaims, user?.idTokenClaims?.roles]);

  useEffect(() => {
    if (userSettings?.DEFAULT_PLANTS?.length > 0
      || userSettings?.DEFAULT_COMPANY_CODES?.length > 0) {
      setDefaultsDisabled(false);
    } else {
      setDefaultsDisabled(true);
    }
  }, [userSettings]);

  const onUseDefaultToggle = (checked) => {
    dispatch(allActions.CommonActions.setUseDefaultSettings(checked));
    dispatch(allActions.WorkListActions.nullRowCountAll());
  };

  const submitImpactForm = ({
    plant, material, impact, impactType, notes, overstock = 0,
  }) => {
    setImpactFormVisible(false);
    dispatch(allActions.CommonActions.submitImpactForm(
      plant,
      material,
      impact,
      impactType,
      notes,
      user?.account?.username,
      overstock,
    ));
  };

  const getTitle = () => {
    let title;
    if (userSettings?.DEFAULT_PLANTS?.length > 0 && plants?.length > 0) {
      const plantNames = {};
      plants.forEach((p) => {
        if (p?.PLANT_FACILITY_SAP_ID && p?.PLANT_FACILITY_SAP_NM) {
          plantNames[p.PLANT_FACILITY_SAP_ID] = p.PLANT_FACILITY_SAP_NM;
        }
      });
      title = userSettings.DEFAULT_PLANTS
        .filter((p) => p && plantNames[p])
        .map((p) => `${p} (${plantNames[p]})`)
        .join('\r\n');
    } else if (userSettings?.DEFAULT_COMPANY_CODES?.length > 0 && companyCodes?.length > 0) {
      const ccNames = {};
      companyCodes.forEach((cc) => {
        if (cc?.Company_Code && cc?.Company_Name) {
          ccNames[cc.Company_Code] = cc.Company_Name;
        }
      });
      title = userSettings.DEFAULT_COMPANY_CODES
        .filter((cc) => cc && ccNames[cc])
        .map((cc) => `${cc} (${ccNames[cc]})`)
        .join('\r\n');
    } else {
      title = 'No plants or company codes are set as default.';
    }
    return <span style={{ whiteSpace: 'pre-line' }}>{title}</span>;
  };

  const onClick = () => {
    dispatch(allActions.AuthActions.setToLimitedViewer());
  };

  return (
    <div className={`${styles.header}`} style={{ backgroundColor: isProd ? 'var(--prod-color)' : 'var(--non-prod-color)' }}>
      <a href="/" style={{ color: 'inherit', textDecoration: 'none' }}>
        <Title
          strong
          className={`${styles.mainHeader} ${styles.gradientText}`}
          style={{
            background: 'linear-gradient(45deg, var(--contrast-color), #F47560)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent', // Fallback for browsers that don't support background-clip
          }}
        >
          IOTA
        </Title>
      </a>
      <div className={`${styles.rightHeader} ${asMobile ? styles.mobileHeaderItems : ''}`}>
        {!isProd && <Button type="primary" onClick={onClick}>limited viewer</Button>}
        <div className={`${styles.rightMenuItems} ${asMobile ? styles.mobileHeaderItems : ''}`}>
          <Tooltip
            title={getTitle}
            placement="bottom"
            overlayInnerStyle={{
              maxHeight: '600px',
              overflowY: 'auto',
              overflowX: 'hidden',
              wordWrap: 'break-word',
              padding: '8px'
            }}
          >
            <div className={`${styles.headerMenuItem} ${styles.defaultPlants}`} onClick={() => onUseDefaultToggle(!useDefaultSettings)}>
              <Switch
                checkedChildren={<CheckOutlined className={styles.toggleIcon} />}
                unCheckedChildren={<CloseOutlined className={styles.toggleIcon} />}
                checked={defaultsDisabled ? false : useDefaultSettings}
                disabled={defaultsDisabled}
              />
              <span className={styles.linkText}>Default plants/CC</span>
            </div>
          </Tooltip>
          <Tooltip title="Register impact">
            <div className={styles.headerMenuItem} onClick={() => setImpactFormVisible(true)}>
              <FormOutlined className={styles.icon} />
              <span className={styles.linkText}>Register</span>
            </div>
          </Tooltip>
          <div
            className={styles.headerMenuItem}
            style={{ backgroundColor: open ? '#00000065' : '' }}
            onClick={() => {
              setOpen(!open);
            }}
            ref={ref}
          >
            <SettingOutlined className={styles.icon} />
            <span className={styles.linkText}>Settings</span>
            <div className={`${styles.dropdown} ${open ? styles.openDropdown : styles.closedDropdown}`} style={{ backgroundColor: isProd ? 'var(--prod-color)' : 'var(--non-prod-color)' }}>
              <Tooltip placement="left" title="Personal Settings">
                <NavLink to="/settings" key="/settings" className={`${styles.menuItem} ${location === '/settings' ? styles.active : ''}`} onClick={() => closeMenu && closeMenu()}>
                  <UserOutlined className={styles.icon} />
                  <span className={styles.linkText}>Personal</span>
                  <div className={styles.indicator} />
                  <div className={styles.bottomDivider} />
                </NavLink>
              </Tooltip>
              {hasConfigAccess && (
              <Tooltip placement="right" title="Admin Settings">
                <NavLink to="/config" key="/config" className={`${styles.menuItem} ${location === '/config' ? styles.active : ''}`} onClick={() => closeMenu && closeMenu()}>
                  <ToolOutlined className={styles.icon} />
                  <span className={styles.linkText}>Admin</span>
                  <div className={styles.indicator} />
                  <div className={styles.bottomDivider} />
                </NavLink>
              </Tooltip>
              )}
              {hasViewerAccess
          && (
            <Tooltip placement="right" title="Configuration Settings">
              <NavLink to="/config-view" key="/config-view" className={`${styles.menuItem} ${location === '/config-view' ? styles.active : ''}`} onClick={() => closeMenu && closeMenu()}>
                <FilterOutlined className={styles.icon} />
                <span className={styles.linkText}>Config</span>
                <div className={styles.indicator} />
                <div className={styles.bottomDivider} />
              </NavLink>
            </Tooltip>
          )}
              <Tooltip placement="right" title="IOTA Help center">
                <a target="_blank" href="https://statoilsrm.sharepoint.com/sites/IOTAHelpcenter" rel="noreferrer" className={styles.menuItem}>
                  <QuestionOutlined className={styles.icon} />
                  <span className={styles.linkText}>Help</span>
                  <div className={styles.indicator} />
                </a>
              </Tooltip>
              {/* hasConfigAccess && (
          <Menu.Item key="/test-center" icon={<ReconciliationOutlined />} className={`${styles.menuItem} ${location === '' ? styles.active : ''}`}>
            <NavLink to="/test-center">Test Center</NavLink>
          </Menu.Item>
        ) */}
            </div>
          </div>
        </div>
        <UserMenuContainer />
        {
        impactFormVisible
        && (
          <ImpactForm
            visible={impactFormVisible}
            onOk={submitImpactForm}
            onCancel={() => setImpactFormVisible(false)}
          />
        )
      }
      </div>
    </div>
  );
}
